import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { SubscriptionData, PlanType } from '../services/stripeService';
import type { SubscriptionStatus } from '../services/subscriptionService';

interface UserStatus {
  subscriptionStatus: 'trial' | 'paid' | 'none';
  hasActiveSubscription: boolean;
  plan: string | null;
  daysRemaining: number | null;
  source: 'stripe' | 'trial' | 'none';
  status: string | null;
  isExpired: boolean;
  // Données Stripe réelles
  stripeData?: {
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    trialEndDate?: Date;
    createdAt: Date;
    updatedAt: Date;
  };
}

export const useSubscription = () => {
  const { userProfile } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<UserStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userProfile?.uid) return;

    // NOUVELLE LOGIQUE : Prioriser les trials avant les abonnements Stripe
    const checkSubscriptionStatus = async () => {
      try {
        // 1. D'abord vérifier les trials (priorité haute)
        const trialStatus = await checkTrialStatus(userProfile.uid);
        
        // Si le trial est actif, on l'utilise directement
        if (trialStatus.subscriptionStatus === 'trial' && trialStatus.hasActiveSubscription) {
          // Trial actif trouvé - priorité haute
          setSubscriptionStatus(trialStatus);
          return;
        }
        
        // 2. Si pas de trial actif (expiré, annulé ou inexistant), vérifier Stripe
        const subscriptionsRef = collection(db, 'stripe_subscriptions');
        const subscriptionsQuery = query(subscriptionsRef, where('userId', '==', userProfile.uid));
        const snapshot = await getDocs(subscriptionsQuery);
        
        if (!snapshot.empty) {
          // Abonnement Stripe trouvé
          const subscription = snapshot.docs[0].data();
          // Abonnement Stripe trouvé après vérification trial
          
          const subscriptionStatus = await validateStripeSubscription(subscription);
          setSubscriptionStatus(subscriptionStatus);
        } else {
          // Aucun abonnement actif trouvé
          // Aucun abonnement actif trouvé
          setSubscriptionStatus(trialStatus); // Utiliser le statut du trial (même s'il est expiré)
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut:', error);
        setSubscriptionStatus({
          subscriptionStatus: 'none',
          hasActiveSubscription: false,
          plan: null,
          daysRemaining: null,
          source: 'none',
          status: 'error',
          isExpired: true
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Écouter les changements dans les trials pour une mise à jour en temps réel
    const trialsRef = collection(db, 'trials');
    const trialsQuery = query(trialsRef, where('userId', '==', userProfile.uid));
    
    const unsubscribe = onSnapshot(trialsQuery, () => {
      checkSubscriptionStatus();
    }, (error) => {
      console.error('Erreur lecture trials:', error);
      checkSubscriptionStatus();
    });

    // Vérification initiale
    checkSubscriptionStatus();

    return unsubscribe;
  }, [userProfile?.uid]);

  // Validation complète d'un abonnement Stripe (même logique que useSubscriptionStatus)
  const validateStripeSubscription = async (subscription: any): Promise<UserStatus> => {
    const currentDate = new Date();
    const endDate = subscription.currentPeriodEnd?.toDate();
    const startDate = subscription.currentPeriodStart?.toDate();
    const createdAt = subscription.createdAt?.toDate() || new Date();
    const updatedAt = subscription.updatedAt?.toDate() || new Date();
    const trialEndDate = subscription.trialEndDate?.toDate();
    
    // Vérifier si l'abonnement est techniquement actif
    const isActive = isStripeSubscriptionActive(subscription, currentDate, endDate);
    
    // Calculer les jours restants
    const daysRemaining = endDate && endDate > currentDate 
      ? Math.max(0, Math.ceil((endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

    return {
      subscriptionStatus: isActive ? 'paid' : 'none',
      hasActiveSubscription: isActive,
      plan: subscription.plan || 'Professional',
      daysRemaining: isActive ? daysRemaining : null,
      source: 'stripe',
      status: subscription.status,
      isExpired: !isActive,
      stripeData: {
        currentPeriodStart: startDate || new Date(),
        currentPeriodEnd: endDate || new Date(),
        trialEndDate,
        createdAt,
        updatedAt
      }
    };
  };

  // Vérifier si un abonnement Stripe est actif (même logique que useSubscriptionStatus)
  const isStripeSubscriptionActive = (subscription: any, currentDate: Date, endDate: Date | null): boolean => {
    // 1. Statut Stripe valide
    const validStatuses = ['active', 'trialing'];
    if (!validStatuses.includes(subscription.status)) {
      return false;
    }
    
    // 2. Date de fin non dépassée
    if (endDate && endDate <= currentDate) {
      return false;
    }
    
    // 3. Pas de cancellation programmée
    if (subscription.cancelAtPeriodEnd) {
      return false;
    }
    
    return true;
  };

  // Vérifier le statut du trial interne (même logique que useSubscriptionStatus)
  const checkTrialStatus = async (userId: string): Promise<UserStatus> => {
    try {
      const trialsRef = collection(db, 'trials');
      const trialsQuery = query(trialsRef, where('userId', '==', userId), where('status', '==', 'active'));
      
      const trialsSnap = await getDocs(trialsQuery);
      
      if (!trialsSnap.empty) {
        const trial = trialsSnap.docs[0].data();
        // Trial actif trouvé
        
        const endDate = trial.endDate?.toDate();
        const currentDate = new Date();
        const isActive = endDate && endDate > currentDate;
        const daysRemaining = isActive && endDate 
          ? Math.max(0, Math.ceil((endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)))
          : 0;
        
        return {
          subscriptionStatus: isActive ? 'trial' : 'none',
          hasActiveSubscription: isActive,
          plan: 'Trial',
          daysRemaining: isActive ? daysRemaining : null,
          source: 'trial',
          status: isActive ? 'active' : 'expired',
          isExpired: !isActive
        };
      } else {
        // Aucun trial actif
        return {
          subscriptionStatus: 'none',
          hasActiveSubscription: false,
          plan: null,
          daysRemaining: null,
          source: 'none',
          status: 'no_trial',
          isExpired: true
        };
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du trial:', error);
      return {
        subscriptionStatus: 'none',
        hasActiveSubscription: false,
        plan: null,
        daysRemaining: null,
        source: 'none',
        status: 'error',
        isExpired: true
      };
    }
  };

  // Adapter les données pour la compatibilité avec les anciens composants
  const subscription: SubscriptionData | null = subscriptionStatus ? {
    userId: userProfile?.uid || '',
    restaurantId: userProfile?.restaurantId || '',
    plan: (subscriptionStatus.plan || 'professional') as PlanType,
    subscriptionStatus: subscriptionStatus.subscriptionStatus === 'paid' 
      ? 'active'
      : subscriptionStatus.subscriptionStatus === 'trial' 
        ? 'trial' 
        : 'cancelled',
    createdAt: subscriptionStatus.stripeData?.createdAt || new Date(),
    updatedAt: subscriptionStatus.stripeData?.updatedAt || new Date(),
    currentPeriodStart: subscriptionStatus.stripeData?.currentPeriodStart,
    currentPeriodEnd: subscriptionStatus.stripeData?.currentPeriodEnd,
    trialEndDate: subscriptionStatus.stripeData?.trialEndDate
  } : null;

  const isExpired = subscriptionStatus ? subscriptionStatus.isExpired : true;
  
  const daysRemaining = subscriptionStatus?.daysRemaining || null;

  const hasActiveSubscription = () => {
    return subscriptionStatus?.hasActiveSubscription || false;
  };

  const canAccessFeature = (feature: string): boolean => {
    if (!hasActiveSubscription()) {
      // Seules certaines routes sont accessibles sans abonnement
      const allowedRoutes = ['/dashboard', '/settings', '/subscription'];
      return allowedRoutes.includes(feature);
    }
    return true;
  };

  const getSubscriptionMessage = (): string | null => {
    if (!subscriptionStatus) return null;

    // Si abonnement payant actif, pas de message
    if (subscriptionStatus.subscriptionStatus === 'paid' && subscriptionStatus.hasActiveSubscription) {
      return null;
    }

    // Si trial actif, afficher les jours restants
    if (subscriptionStatus.subscriptionStatus === 'trial' && subscriptionStatus.daysRemaining !== null) {
      if (subscriptionStatus.daysRemaining > 0) {
        return `Essai gratuit - ${subscriptionStatus.daysRemaining} jour${subscriptionStatus.daysRemaining > 1 ? 's' : ''} restant${subscriptionStatus.daysRemaining > 1 ? 's' : ''}`;
      } else {
        return 'Votre essai gratuit a expiré. Choisissez un plan pour continuer.';
      }
    }

    // Aucun abonnement actif
    if (!subscriptionStatus.hasActiveSubscription) {
      return 'Votre abonnement a expiré. Renouvelez pour accéder à toutes les fonctionnalités.';
    }

    return null;
  };

  const refreshSubscription = () => {
    // Pas besoin de fonction de refresh car on utilise onSnapshot qui écoute en temps réel
  };

  return {
    subscription,
    subscriptionStatus,
    subscriptionType: subscriptionStatus?.subscriptionStatus || null,
    canStartTrial: false, // Cette logique n'est plus gérée ici
    isLoading,
    isExpired,
    daysRemaining,
    hasActiveSubscription: hasActiveSubscription(),
    canAccessFeature,
    getSubscriptionMessage,
    refreshSubscription
  };
};
